import React, { useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import _ from 'lodash';
import styles from './AssetsSlider.module.scss';
import { useWindowSize } from 'src/utils/window';
import { AssetEntity, AssetType } from 'src/modules/assets';
import { classes } from 'src/utils/styles';
import { makeApiUrl } from 'src/utils/url';
import { FormattedMessage } from 'react-intl';
import fileSize from 'filesize';
import dynamic from 'next/dynamic';
import AssetsSliderImage from './image/AssetsSliderImage';
import { BubbleIcon } from 'src/modules/global/components/bubbleIcon/BubbleIcon';
import { IconImage } from 'src/modules/global';
import ReactDOM from 'react-dom';
import { Icon } from 'src/modules/global/components/icon/Icon';

const speed = 500;

export const isPreviewSupported = (asset: AssetEntity) => {
  return [AssetType.Image, AssetType.PDF].includes(asset.type);
};

const AssetsSliderPdf = dynamic(() => import('./pdf/AssetsSliderPdf'), {
  ssr: false,
});

interface AssetsSliderProps {
  activeAsset: AssetEntity;
  assets: AssetEntity[];
}

const AssetsSlider: React.FunctionComponent<AssetsSliderProps> = (props) => {
  const { activeAsset: initialActiveAsset, assets } = props;
  const initialSlideIndex = useMemo(() => {
    const index = assets.findIndex((a) => a.id === initialActiveAsset.id);
    return index !== -1 ? index : 0;
  }, [assets, initialActiveAsset.id]);

  // adaptive height needs to toggle from false to true for PDFs, otherwise react-slick doesn't know how to
  // adapt its height
  const [isAdaptiveHeight, setAdaptiveHeight] = useState<boolean>(
    assets.length && assets[0].type === AssetType.PDF ? false : true
  );

  const [activeAsset, setActiveAsset] = useState(initialActiveAsset);
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);
  const [isBlocked, setIsBlocked] = useState(false);

  const slider = useRef<Slider>();

  const { width } = useWindowSize();

  const unblock = () => {
    setIsBlocked(true);
    setTimeout(() => {
      setIsBlocked(false);
    }, speed);
  };

  const handleNext = () => {
    if (isBlocked) {
      return;
    }
    unblock();
    let nextIndex = currentIndex + 1;
    if (nextIndex >= assets.length) {
      nextIndex = nextIndex % assets.length;
    }

    slider.current.slickNext();
    setActiveAsset(assets[nextIndex]);
    setCurrentIndex(nextIndex);
  };
  const handlePrev = () => {
    if (isBlocked) {
      return;
    }
    unblock();

    let nextIndex = currentIndex - 1;
    if (nextIndex < 0) {
      nextIndex = assets.length - 1;
    }

    slider.current.slickPrev();
    setActiveAsset(assets[nextIndex]);
    setCurrentIndex(nextIndex);
  };

  const hideNextPrevButtons = activeAsset?.type === 'pdf';

  const imageSlideHeight = slider.current
    ? // eslint-disable-next-line react/no-find-dom-node
      Math.max(0, (ReactDOM.findDOMNode(slider.current) as any).offsetHeight - 48 * 2)
    : 0;

  return (
    <div className={styles.wrapper}>
      {!hideNextPrevButtons && (
        <BubbleIcon
          className={classes([styles.navButton, styles.navButtonPrev])}
          onClick={handlePrev}
          iconProps={{ image: IconImage.LeftArrow }}
        />
      )}
      {!hideNextPrevButtons && (
        <BubbleIcon
          className={classes([styles.navButton, styles.navButtonNext])}
          onClick={handleNext}
          iconProps={{ image: IconImage.RightArrow }}
        />
      )}

      <div className={styles.header}>
        <h4 className={styles.fileName}>{activeAsset.file?.caption || activeAsset.file?.name}</h4>

        <div
          className={styles.downloadButton}
          onClick={() => (window.location.href = makeApiUrl(`/assets/download/${activeAsset.id}`))}>
          <span className={styles.downloadButtonText}>
            <FormattedMessage id="products/downloads/listing/action/download" />{' '}
            <span className={styles.downloadButtonFileData}>
              ({fileSize(activeAsset.file?.size * 1000)},{' '}
              {_.trim(activeAsset.file?.ext.toUpperCase(), '.')})
            </span>
          </span>
          <Icon image={IconImage.Details} rotation={90} className={styles.downloadIcon} />
        </div>
      </div>

      <Slider
        ref={slider}
        dots={false}
        arrows={false}
        infinite={true}
        centerMode={false}
        slidesToShow={1}
        slidesToScroll={1}
        variableWidth={false}
        adaptiveHeight={isAdaptiveHeight}
        speed={speed}
        initialSlide={initialSlideIndex}
        className={styles.sliderWrapper}>
        {assets.map((a, i) => (
          <div
            key={a.id}
            data-index={i}
            className={classes([
              styles.slideWrapper,
              a.id === activeAsset.id ? styles.active : null,
            ])}
            style={{ width: `${Math.max(900, width)}px` }}>
            {a.type === AssetType.Image && (
              <AssetsSliderImage asset={a} height={imageSlideHeight} />
            )}
            {a.type === AssetType.PDF && (
              <AssetsSliderPdf
                asset={a}
                onLoad={() => {
                  if (i === currentIndex) {
                    setTimeout(() => setAdaptiveHeight(true), 1000);
                  }
                }}
              />
            )}
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default AssetsSlider;
